import { useEffect, useRef, type RefObject } from 'react';

/**
 * Scroll-reveal hook (progressive enhancement) for the light marketing half.
 *
 * Contract — mirrors the landing's `useLandingMotion` so the SSG prerender is
 * always shown:
 *   - The element renders with the `.sx-reveal` class (opacity:1) in SSR/no-JS,
 *     so the prerendered HTML is NEVER hidden. This hook only opts an element
 *     into the hide-then-animate behaviour AFTER hydration.
 *   - `prefers-reduced-motion` → no-op (stays visible).
 *   - Above/in-the-fold on mount → stays visible (no hide-flash — protects the
 *     first paint / LCP the same way the landing marks in-viewport `.reveal`s).
 *   - Below the fold → after a transition probe confirms CSS transitions
 *     actually tick (capture sandboxes / throttled tabs freeze them, which
 *     would otherwise strand content at opacity:0), the element is armed
 *     (`.sx-reveal-armed`) and revealed (`.sx-reveal-in`) on intersection.
 *
 * @param delayMs - optional stagger delay applied to the reveal transition.
 */
export function useReveal<T extends HTMLElement = HTMLDivElement>(delayMs = 0): RefObject<T> {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const viewportHeight = (): number =>
      window.innerHeight || document.documentElement.clientHeight || 0;

    // Above/within the fold at mount → keep visible, no animation.
    if (el.getBoundingClientRect().top < viewportHeight()) return;

    let io: IntersectionObserver | null = null;
    let cancelled = false;

    // Motion probe: only enable hide+animate when transitions actually tick.
    const probe = document.createElement('div');
    probe.style.cssText =
      'position:fixed;left:-9999px;top:0;width:8px;height:8px;opacity:0;transition:opacity .12s linear;pointer-events:none';
    document.documentElement.appendChild(probe);
    void probe.offsetWidth;
    probe.style.opacity = '1';

    const timer = window.setTimeout(() => {
      const ticks = parseFloat(getComputedStyle(probe).opacity) > 0.02;
      probe.remove();
      const node = ref.current;
      if (cancelled || !ticks || !node) return;
      // Re-check the fold: the user may have scrolled during the probe window.
      if (node.getBoundingClientRect().top < viewportHeight()) return;

      if (delayMs > 0) node.style.transitionDelay = `${delayMs}ms`;
      node.classList.add('sx-reveal-armed');
      io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              entry.target.classList.add('sx-reveal-in');
              io?.unobserve(entry.target);
            }
          }
        },
        { rootMargin: '0px 0px -10% 0px', threshold: 0.01 },
      );
      io.observe(node);
    }, 55);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      io?.disconnect();
      probe.remove();
    };
  }, [delayMs]);

  return ref;
}
